import React, { useState, useMemo } from "react";
import { ModalCompAdvance } from "../../../../components";
import { FiDollarSign, FiPieChart } from "react-icons/fi";
import CategoryPanel from "../../../../components/MemberProfile/CategoryPanel";
import { convertToTitleCase } from "../../../../utils/helpers";
import ContributionEntriesModal from "../ContributionEntriesModal";
import { useGetMemberContributionHistoryQuery } from "../../../../store/member/member-contribution";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";

const KIND_FILTER = {
  savings: "savings",
  shares: "shares",
  other: "other",
};

const resolveCategoryKind = (category = {}) => {
  const raw = (category.category_kind || "").toString().toLowerCase().trim();
  if (raw === "savings" || raw === "shares" || raw === "other") return raw;
  // Legacy fallback before kind backfill
  return Number(category.is_withdrawable) === 1 ? "savings" : "other";
};

/**
 * Category breakdown cards for savings | shares | other.
 * Default kind = savings (withdrawable capital).
 */
const SavingsTab = ({
  savings = [],
  total = 0,
  memberId = null,
  isMemberContext = false,
  kind = "savings",
  title = "Savings Breakdown",
  emptyTitle = "No savings recorded yet",
  emptyMessage = "Savings payments for this member will show up here.",
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const filterType = KIND_FILTER[kind] || "savings";

  const shouldFetchHistory =
    isMemberContext && (!savings || savings.length === 0) && Number(total) > 0;

  const {
    data: contributionHistoryData,
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory,
  } = useGetMemberContributionHistoryQuery(
    {
      page: 1,
      limit: 1000,
      filter_type: filterType,
    },
    { skip: !shouldFetchHistory }
  );

  const groupedSavings = useMemo(() => {
    if (savings && savings.length > 0) {
      return savings;
    }

    const contributionsList =
      contributionHistoryData?.contributions ||
      contributionHistoryData?.histories ||
      [];

    if (!contributionsList.length) return [];

    const grouped = {};

    contributionsList.forEach((contribution) => {
      const category =
        contribution?.contribution_category || contribution?.category || {};
      const categoryKind = resolveCategoryKind(category);
      if (categoryKind !== kind) return;

      const categoryId =
        contribution?.contribution_category_id ||
        contribution?.contribution_category?.id ||
        contribution?.category_id ||
        contribution?.category?.id ||
        "unknown";

      const categoryName =
        contribution?.contribution_category?.name ||
        contribution?.category?.name ||
        contribution?.contribution_category_name ||
        contribution?.category_name ||
        "Unknown Category";

      if (!grouped[categoryId]) {
        grouped[categoryId] = {
          category_id: categoryId,
          category_name: categoryName,
          total_amount: 0,
          amount_remaining: null,
          threshold: category?.threshold || null,
          is_withdrawable: kind === "savings" ? 1 : 0,
          category_kind: kind,
          entries: [],
        };
      }

      grouped[categoryId].total_amount += parseFloat(contribution?.amount || 0);

      let paymentMethodName = contribution?.payment_method;
      if (typeof paymentMethodName === "object" && paymentMethodName?.name) {
        paymentMethodName = paymentMethodName.name;
      } else if (typeof paymentMethodName === "string") {
        paymentMethodName = paymentMethodName.replace(/_/g, " ");
      }

      grouped[categoryId].entries.push({
        id: contribution?.id,
        amount: contribution?.amount,
        payment_date: contribution?.payment_date || contribution?.created_at,
        payment_method: paymentMethodName,
        status: contribution?.status,
        reference: contribution?.reference,
      });
    });

    return Object.values(grouped);
  }, [savings, contributionHistoryData, kind]);

  const handleOpen = (category) => {
    setSelectedCategory({ ...category, type: kind });
  };

  const handleClose = () => setSelectedCategory(null);

  if (isLoadingHistory || isFetchingHistory) {
    return (
      <div className="py-5" aria-busy="true">
        <TableSkeletonLoader rows={3} columns={2} />
      </div>
    );
  }

  return (
    <div className="w-full">
      <CategoryPanel
        groups={groupedSavings || []}
        total={total}
        title={title}
        tone={kind === "shares" ? "purple" : "green"}
        icon={kind === "shares" ? FiPieChart : FiDollarSign}
        totalLabel={kind === "savings" ? "Net savings" : "Total shares"}
        summary={kind === "savings" ? "Total is net of withdrawals" : undefined}
        emptyTitle={emptyTitle}
        emptyText={emptyMessage}
        missingTitle={`Couldn't load the ${kind} breakdown`}
        onOpen={handleOpen}
        onRetry={() => window.location.reload()}
      />

      <ModalCompAdvance
        show={!!selectedCategory}
        handleClose={handleClose}
        close
        size="lg"
        centered
        modalBody={0}
        arrayComp={[
          {
            component:
              selectedCategory && (
                <ContributionEntriesModal
                  type={kind}
                  categoryName={convertToTitleCase(
                    selectedCategory.category_name || "Unknown Category"
                  )}
                  total={selectedCategory.total_amount || 0}
                  entries={selectedCategory.entries || []}
                  onClose={handleClose}
                />
              ),
          },
        ]}
      />
    </div>
  );
};

export default SavingsTab;
